/**
 * `/v1/organization/members` — the Organization tab.
 *
 * The same people as the contact register, filtered by membership and enriched with org role,
 * responsibility and the simple "reports to" field the plan asks for. It is a **field**, not an
 * org chart: one select pointing at another membership, and nothing that draws boxes and lines.
 */
import {
  organizationMemberDtoSchema,
  organizationMemberListResponseSchema,
  removeOrganizationMemberResponseSchema,
  type OrganizationMemberDto,
  type OrganizationMemberListResponse,
  type RemoveOrganizationMemberResponse,
  type UpdateOrganizationMemberRequest,
} from '@tvitvi/shared';
import { queryOptions, useMutation, type UseMutationResult } from '@tanstack/react-query';

import { apiRequest } from '@/api/client';
import { CACHE } from '@/api/cache-tiers';
import { qk, type MemberListFilters } from '@/api/query-keys';

const PAGE_SIZE = 100;

/**
 * The members list, ordered by the server.
 *
 * `role` is not the same rows in a different order: the API drives that query from
 * `org_memberships` rather than from `contacts`, because that is where the index is, and its
 * keyset tiebreaker is the person id rather than the contact id. That is safe precisely because a
 * cursor records the sort it was minted for — and it is why a sort change here starts the list
 * again rather than continuing from a position that no longer means anything. The sort is part of
 * `queryKey`, so that happens by construction.
 */
export function membersQueryOptions(orgId: string, filters: MemberListFilters) {
  return queryOptions({
    queryKey: qk.memberList(orgId, filters),
    queryFn: ({ signal }): Promise<OrganizationMemberListResponse> =>
      apiRequest({
        path: '/organization/members',
        query: {
          sort: filters.sort ?? 'name',
          direction: filters.direction ?? 'asc',
          limit: PAGE_SIZE,
          search: filters.search,
          /* Sent only when it is not the default, so «Aktive» leaves no mark on the request. */
          ...(filters.status === 'deactivated' ? { status: 'deactivated' } : {}),
          /* F53: filter på rolle. */
          ...(filters.role === undefined ? {} : { role: filters.role }),
          /* 253.69: «Plattformadminer» er et filter her, ikke en egen side. */
          ...(filters.platformAdmins === true ? { platformAdmins: 'true' } : {}),
        },
        schema: organizationMemberListResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

export interface UpdateMemberInput {
  membershipId: string;
  body: UpdateOrganizationMemberRequest;
}

/**
 * Role, responsibility and who this person reports to.
 *
 * Decision 0007 again: the resolver puts the requested role on the resource, so `can()` refuses
 * `cannot_grant_higher_role` before the service exists, and the database refuses any transaction
 * that would leave the organization without a superadmin (`LAST_SUPERADMIN`).
 */
export function useUpdateMember(
  orgId: string,
): UseMutationResult<OrganizationMemberDto, unknown, UpdateMemberInput> {
  return useMutation({
    mutationFn: ({ membershipId, body }: UpdateMemberInput) =>
      apiRequest({
        path: `/organization/members/${membershipId}`,
        method: 'PATCH',
        body,
        schema: organizationMemberDtoSchema,
      }),
    meta: { invalidates: [qk.members(orgId), qk.contacts(orgId)] },
  });
}

/**
 * DE TRE DØRENE UT AV HUSET OG INN IGJEN (QA15 blokk B2).
 *
 * Alle tre ligger bak `membership:delete`, og alle tre svarer det samme settet med nøkler ut:
 * medlemslista OG kontaktregisteret, fordi den ene fana er et blikk på den andre. Invitasjonene
 * er med på fjerningen alene — en ventende invitasjon kan ha pekt på den som nå er borte som
 * «rapporterer til», og serveren nullstiller den i samme transaksjon.
 *
 * `qk.members(orgId)` treffer begge statuslistene: den er prefikset til `memberList`, og
 * `invalidateQueries` matcher på prefiks. Derfor dukker den deaktiverte opp under «Deaktiverte»
 * i det hen forsvinner fra «Aktive», uten at noen av kallstedene må vite om begge.
 */
export function useDeactivateMember(
  orgId: string,
): UseMutationResult<OrganizationMemberDto, unknown, string> {
  return useMutation({
    mutationFn: (membershipId: string) =>
      apiRequest({
        path: `/organization/members/${membershipId}/deactivate`,
        method: 'POST',
        schema: organizationMemberDtoSchema,
      }),
    meta: { invalidates: [qk.members(orgId), qk.contacts(orgId)] },
  });
}

export function useReactivateMember(
  orgId: string,
): UseMutationResult<OrganizationMemberDto, unknown, string> {
  return useMutation({
    mutationFn: (membershipId: string) =>
      apiRequest({
        path: `/organization/members/${membershipId}/reactivate`,
        method: 'POST',
        schema: organizationMemberDtoSchema,
      }),
    meta: { invalidates: [qk.members(orgId), qk.contacts(orgId)] },
  });
}

export function useRemoveMember(
  orgId: string,
): UseMutationResult<RemoveOrganizationMemberResponse, unknown, string> {
  return useMutation({
    mutationFn: (membershipId: string) =>
      apiRequest({
        path: `/organization/members/${membershipId}`,
        method: 'DELETE',
        schema: removeOrganizationMemberResponseSchema,
      }),
    meta: {
      invalidates: [qk.members(orgId), qk.contacts(orgId), qk.invitations(orgId), qk.tasks(orgId)],
    },
  });
}
